import { type FC, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useToken } from "@packsize/authentication";

import { ProductionContext } from "@/contexts/production";
import { updateAllSolutionAttributeValuesToString } from "@/views/production/saved-jobs/utils/solution-transforms";
import { getSolutionTemplate } from "@/views/production/saved-jobs/utils/solution-template";
import { usePackagingSolutionPackagingDesigns } from "@/views/production/saved-jobs/hooks/use-packaging-solution-packaging-designs";
import { useUserFavoriteDesigns } from "@/views/production/saved-jobs/hooks/use-user-favorite-designs";
import { useSubmitPackagingSolution } from "@/views/production/saved-jobs/hooks/use-submit-packaging-solution";
import { RULE_EXECUTION_SOURCE } from "@/views/configuration/importing-data/utils/constants";
import type { Location, LocationOption } from "@/views/production/saved-jobs/types/types";
import { useBrands, useFlutes } from "@/hooks/corrugates";
import { emptyGuid } from "@/constants";
import { SavedJobDependencyContext } from "@/views/production/saved-jobs/contexts/saved-job-dependency-context";
import { useEmMachine } from "@/hooks/machines/em";
import type {
	DesignParameter,
	PackagingComponentCartonSaved,
	PackagingComponentLabelSaved,
	SavedPackagingSolution,
} from "@/schemas";

interface SavedJobDependencyProviderProps {
	readonly children: ReactNode;
}

export const SavedJobDependencyProvider: FC<SavedJobDependencyProviderProps> = ({ children }) => {
	const intl = useIntl();
	const { currentMachineGroup, currentProductionGroup } = useContext(ProductionContext);
	const { decodedToken } = useToken();
	const {
		packagingDesigns,
		error: loadPackagingDesignsError,
		isLoading: isLoadingPackagingDesigns,
		refetch: refetchPackagingDesigns,
	} = usePackagingSolutionPackagingDesigns();
	const { flutes, isLoading: isLoadingFlutes, error: flutesError, refetch: refetchFlutes } = useFlutes();
	const { brands, isLoading: isLoadingBrands, error: brandsError, refetch: refetchBrands } = useBrands();
	const {
		userFavoriteDesigns,
		isLoading: isLoadingUserFavoriteDesigns,
		error: userFavoriteDesignsError,
		refetch: refetchUserFavoriteDesigns,
	} = useUserFavoriteDesigns();
	const [selectedPackStationKey, setSelectedPackStationKey] = useState<string | undefined>();
	const submitPackagingSolutionMutation = useSubmitPackagingSolution();

	const emMachineId = useMemo(() => {
		if (currentMachineGroup === null) return null;
		const configuredMachines = currentMachineGroup?.configuredMachinesWithMachineType ?? {};
		for (const [machineId, machineType] of Object.entries(configuredMachines)) {
			if (machineType === "Em") {
				return machineId;
			}
		}
		return null;
	}, [currentMachineGroup]);

	const { data: emMachineData } = useEmMachine(emMachineId);

	const locationOptions = useMemo<LocationOption[]>(() => {
		if (!emMachineData?.crossConveyorConnected) return [];
		return [
			{
				key: `${currentMachineGroup?.id}_1`,
				text: `${emMachineData.alias} - 1`,
				value: {
					packStation: 1,
					machine: emMachineData.id,
					machineGroup: currentMachineGroup?.alias,
					productionGroup: currentProductionGroup?.alias,
					pickZone: null,
				},
			},
			{
				key: `${currentMachineGroup?.id}_2`,
				text: `${emMachineData.alias} - 2`,
				value: {
					packStation: 2,
					machine: emMachineData.id,
					machineGroup: currentMachineGroup?.alias,
					productionGroup: currentProductionGroup?.alias,
					pickZone: null,
				},
			},
		];
	}, [emMachineData, currentMachineGroup, currentProductionGroup?.alias]);

	const packStationKey = useMemo(() => {
		if (selectedPackStationKey && locationOptions.some((opt) => opt.key === selectedPackStationKey)) {
			return selectedPackStationKey;
		}
		return locationOptions.length > 0 ? locationOptions[0].key : undefined;
	}, [selectedPackStationKey, locationOptions]);

	const location = useMemo<Location>(() => {
		if (packStationKey) {
			const selectedOption = locationOptions.find((opt) => opt.key === packStationKey);
			if (selectedOption) return selectedOption.value;
		}
		return {
			packStation: null,
			machine: null,
			machineGroup: currentMachineGroup?.alias,
			productionGroup: currentProductionGroup?.alias,
			pickZone: null,
		};
	}, [locationOptions, packStationKey, currentMachineGroup?.alias, currentProductionGroup?.alias]);

	const setLocation = useCallback(
		(value: Location) => {
			const option = locationOptions.find(
				(opt) => opt.value.packStation === value.packStation && opt.value.machine === value.machine,
			);
			setSelectedPackStationKey(option?.key);
		},
		[locationOptions],
	);

	const setPackStationKey = useCallback((key: string | undefined) => {
		setSelectedPackStationKey(key);
	}, []);

	const sendToQueuev2 = useCallback(
		async (solutionToSend: SavedPackagingSolution) => {
			const solutionWithLocation: SavedPackagingSolution = {
				...solutionToSend,
				location: { ...location, packStation: location.packStation?.toString() },
				quantity: parseInt(solutionToSend.attributes.quantity ?? "1"),
				batchId: null,
				internalUniqueId: emptyGuid,
				executionSource: RULE_EXECUTION_SOURCE.PACKAGING_CREATION,
			};
			solutionWithLocation.attributes.source = "PackageCreation";

			const toSend = updateAllSolutionAttributeValuesToString(solutionWithLocation);
			await submitPackagingSolutionMutation.mutateAsync([toSend]);
		},
		[submitPackagingSolutionMutation, location],
	);

	const sendToQueue = useCallback(
		async (solutionToSend: SavedPackagingSolution, quantity: string) => {
			solutionToSend.attributes.quantity = quantity;
			await sendToQueuev2(solutionToSend);
		},
		[sendToQueuev2],
	);

	const refetchDependencies = useCallback(async () => {
		await Promise.all([refetchFlutes(), refetchBrands(), refetchUserFavoriteDesigns()]);
	}, [refetchFlutes, refetchBrands, refetchUserFavoriteDesigns]);

	const isLoadingDependencies = isLoadingFlutes || isLoadingBrands || isLoadingUserFavoriteDesigns;
	const errorDependencies = flutesError || brandsError || userFavoriteDesignsError;

	const sendSegmentedCartonSolution = useCallback(
		async (carton: PackagingComponentCartonSaved, solutionAlias: string, isPrimary = false) => {
			let cartonName = carton.media.attributes?.name ?? "";

			if (!cartonName) {
				cartonName = isPrimary
					? intl.formatMessage({ id: "Primary Carton", defaultMessage: "Primary Carton" })
					: intl.formatMessage({ id: "Untitled Carton", defaultMessage: "Untitled Carton" });
			}

			await sendToQueuev2({
				...getSolutionTemplate(decodedToken!.tid),
				cartons: [carton],
				alias: `${solutionAlias} - ${cartonName}`,
			});
		},
		[decodedToken, sendToQueuev2, intl],
	);

	const sendSegmentedLabelSolution = useCallback(
		async (label: PackagingComponentLabelSaved) => {
			await sendToQueuev2({
				...getSolutionTemplate(decodedToken!.tid),
				labels: [label],
			});
		},
		[decodedToken, sendToQueuev2],
	);

	const lookupDesignName = useCallback(
		(designId: number): string =>
			packagingDesigns.find((pd) => pd.designId === designId)?.designName ?? String(designId),
		[packagingDesigns],
	);

	const lookupDesignXValues = useCallback(
		(designId: number): DesignParameter[] =>
			packagingDesigns
				.find((pd) => pd.designId === designId)
				?.designParameters.sort((p1, p2) => {
					return p1.name.localeCompare(p2.name, undefined, {
						numeric: true,
					});
				}) ?? [],
		[packagingDesigns],
	);

	const contextValue = useMemo(
		() => ({
			locationOptions,
			location,
			packStationKey,
			setPackStationKey,
			setLocation,
			flutes,
			brands,
			packagingDesigns,
			lookupDesignName,
			lookupDesignXValues,
			sendToQueue,
			sendToQueuev2,
			sendSegmentedCartonSolution,
			sendSegmentedLabelSolution,
			isLoadingDependencies: isLoadingDependencies || isLoadingPackagingDesigns,
			refetchDependencies: () => {
				refetchDependencies();
				refetchPackagingDesigns();
			},
			errorDependencies: errorDependencies || loadPackagingDesignsError,
			userFavoriteDesigns,
			refetchUserFavoriteDesigns,
		}),
		[
			locationOptions,
			location,
			packStationKey,
			setPackStationKey,
			setLocation,
			flutes,
			brands,
			packagingDesigns,
			lookupDesignName,
			lookupDesignXValues,
			sendToQueue,
			sendToQueuev2,
			sendSegmentedCartonSolution,
			sendSegmentedLabelSolution,
			isLoadingDependencies,
			isLoadingPackagingDesigns,
			errorDependencies,
			loadPackagingDesignsError,
			userFavoriteDesigns,
			refetchUserFavoriteDesigns,
			refetchDependencies,
			refetchPackagingDesigns,
		],
	);

	return <SavedJobDependencyContext.Provider value={contextValue}>{children}</SavedJobDependencyContext.Provider>;
};
